import { Container, Sprite, Texture } from "pixi.js"
import { TreeArt, crownBreakupOrder, crownReach, fallbackTreeArt } from "./tree_art"
import { TreeDebris, between, pick } from "./tree_debris"
import { FRESH_WOOD } from "./tree_hit"
import { treeFallFeedback } from "../../../../effects/tree_fall_feedback"

/**
 * A felled tree going over.
 *
 * This replaced a crumble: the tree's art diced into eight random rectangles that sprayed out in every
 * direction, spun, bounced off a line through the middle of the trunk and faded. It read as the tree
 * EXPLODING -- the opposite of what a chopped tree does -- and it threw away the one moment the whole
 * run of axe strokes had been building up to.
 *
 * What happens now, in order:
 *
 *   THE CUT GIVES. The art is cut in two at the axe's notch (tree_art.fellTreeArt): a CROWN above the
 *   cut and a STUMP below it, the stump's top painted as a flat face of fresh pale wood. The crown turns
 *   on a hinge at the far side of the trunk, AWAY from whoever felled it -- carrying on from the lean
 *   the last blow left it in, so there is no snap back to upright first.
 *
 *   IT TOPPLES. A rigid body pivoting at its foot: angular acceleration grows with the sine of its lean,
 *   so it creaks over slowly, then gathers speed, then comes down fast -- the timber fall everyone knows,
 *   about a second from first give to the ground. A burst of chips comes out of the hinge as it breaks,
 *   and leaves stream off the canopy in proportion to how fast it is moving.
 *
 *   IT LANDS. At the bottom the butt comes off the stump and drops to the ground. The crash throws dust
 *   out from under its whole length, kicks the canopy's leaves up and scatters twigs; it bounces once
 *   and settles. The crash is also a camera kick and a sound, through tree_fall_feedback, scaled by how
 *   near the listener is.
 *
 *   IT KEEPS ITS SHAPE. The crown is turned, never scaled: flattening it as it lay down and squashing it
 *   on the crash were both tried, and in the game both read as the tree warping rather than falling.
 *
 *   IT CLEARS. The moment it hits, still bouncing, the crown starts to break up from its far end back to
 *   the butt, leaves lifting off where it comes apart; the stump stays a little longer and then
 *   sinks away too. The tile has already gone from the map (the server removed it
 *   when the blow landed), so all of this is the client's own sprites, and none of it blocks anything.
 *
 * Art that could not be read (a headless renderer, an unreadable texture) still falls, as the whole
 * picture tipping over from its foot, with no stump.
 *
 * NO TIMERS. update() off the render loop advances every fall; particles go into the same TreeDebris
 * pool as the chop's, under the same cap.
 *
 * LIVE TUNING, from the browser console: window.treeFall.{enabled, gravity, bounce, restMs, fadeMs, stumpMs, ...}
 */

export interface FelledTree {
    art: TreeArt
    // The first row of the stump, and the hinge's column, in the art.
    cutY: number
    hingeX: number
    // The art cut in two, each the full art's size, so both draw at the art's own top-left.
    crown: Texture
    stump: Texture
    shadow?: Texture
    // The crown breaking up as it clears away: `stages` steps before it is gone, each baked on first use
    // (see crownBreakupOrder).
    breakup?: { stages: number; stage: (index: number) => Texture }
}

export interface TreeFelling {
    // Identifies the tree: a second felling of the same key replaces the first.
    key: string
    // The standing tree as the map drew it. Read for position, size, depth and alpha; never moved.
    sprites: Sprite[]
    // The tree cut in two (TreeDamage.felled). Undefined: topple the whole picture from its foot.
    felled?: FelledTree
    // +1 falls to the right, -1 to the left. See fallSide.
    side: number
    // The lean the last blow left it in, as TreeHit measures it (px over per px up).
    lean?: number
}

export interface TreeFallTuning {
    enabled: boolean
    // Angular acceleration per unit of sin(lean), 1/s^2. Sets the pace of the whole fall: 12 lands a
    // tree a little under a second after it starts to go.
    gravity: number
    // How fast it starts over, rad/s. Fixed rather than taken from the last blow so the creak at the
    // start lasts the same however the blow and the felling arrived.
    startSpeed: number
    // Share of its speed the crown keeps when it rebounds off the ground.
    bounce: number
    // How long after it first hits the ground the felled tree starts to break up (even mid-bounce), how
    // long the break-up takes, and how long the stump stays after the crash before it sinks away.
    restMs: number
    fadeMs: number
    stumpMs: number
    // Leaves shed in the fall, per radian it travels; and leaves, dust and twigs thrown up by the crash.
    trail: number
    leaves: number
    dust: number
    // Chance a canopy point that breaks up this frame lets a leaf go.
    breakupLeaves: number
}

export interface Fall {
    key: string
    side: number
    crown: Container
    // The cut crown's one sprite, whose texture steps through the break-up. Undefined for art that
    // could not be cut, which fades instead.
    piece?: Sprite
    breakup?: FelledTree["breakup"]
    stump?: Sprite
    shadow?: Sprite
    shadowAlpha: number
    art: TreeArt
    // The art's top-left in the world.
    left: number
    top: number
    /** How far the art is drawn up a hill (CompositeTileRenderer's `hillLift`): where it falls is that far further south. */
    hillLift: number
    // The hinge, in the art and in the world as the tree stood.
    hingeArtX: number
    hingeArtY: number
    hingeX: number
    hingeY: number
    // How far the butt drops when it comes off the stump: the cut's height above the roots.
    drop: number
    angle: number
    velocity: number
    snapped: boolean
    impacts: number
    // When it first hit the ground, and when it came to rest there after bouncing.
    hitMs: number
    landedMs: number
    fadeStartMs: number
    stumpFadeMs: number
    alphaFrom: number
    bornMs: number
    zFloor: number
    owed: number
}

// The same integration step and stall guard as TreeHit.
const STEP = 1 / 240
const MAX_FRAME_SECONDS = 0.05
const QUARTER = Math.PI / 2
// When the hinge gives way: a few degrees over, when the creak is done and it is truly going.
const SNAP_ANGLE = 0.16
// Slower than this at the ground and it is down to stay.
const SETTLE_SPEED = 0.9
// The stump's sink at the end.
const STUMP_FADE_MS = 900
// Above this many trees falling at once, the newest just goes: a crowd of woodcutters is not worth a
// frame spent on it.
const MAX_FALLS = 10

const DUST = 0xcfbf9c
const DIRT = 0x9a8463

const smoothstep = (a: number, b: number, x: number) => {
    const t = Math.max(0, Math.min(1, (x - a) / (b - a)))
    return t * t * (3 - 2 * t)
}

/**
 * Which way a tree falls: away from whoever felled it. Struck from dead ahead, it goes the way the last
 * blow was already swinging it, and failing that either way.
 */
export const fallSide = (trunkX: number, from?: { x: number }, lean?: number): number => {
    const dx = from ? trunkX - from.x : 0
    if (Math.abs(dx) >= 4) return Math.sign(dx)
    if (lean && Math.abs(lean) > 1e-4) return Math.sign(lean)
    return Math.random() < 0.5 ? -1 : 1
}

export class TreeFall {
    readonly tuning: TreeFallTuning = {
        enabled: true,
        gravity: 12,
        startSpeed: 0.35,
        bounce: 0.24,
        restMs: 0,
        fadeMs: 650,
        stumpMs: 3200,
        trail: 7,
        leaves: 22,
        dust: 10,
        breakupLeaves: 0.6,
    }

    private surface: Container
    private debris: TreeDebris
    private falls = new Map<string, Fall>()
    private lastMs?: number

    constructor(surface: Container, debris: TreeDebris) {
        this.surface = surface
        this.debris = debris
        if (typeof window !== "undefined") {
            ;(window as any).treeFall = this.tuning
        }
    }

    setSurface(surface: Container): void {
        this.surface = surface
    }

    // Exposed for tests.
    isFalling = (key: string) => this.falls.has(key)
    fallOf = (key: string): Readonly<Fall> | undefined => this.falls.get(key)

    /** Bring a tree down. Returns false when there was nothing on screen to fell. */
    fell = (felling: TreeFelling, nowMs: number = performance.now()): boolean => {
        if (!this.tuning.enabled) return false
        const set = felling.sprites.filter(s => s && !s.destroyed && !!s.transform)
        if (!set.length) return false
        this.remove(felling.key)
        if (this.falls.size >= MAX_FALLS) return false

        let left = Infinity
        let top = Infinity
        let right = -Infinity
        let bottom = -Infinity
        let treeZ = -Infinity
        let alpha = 0
        let hillLift = 0
        for (const s of set) {
            hillLift = Math.max(hillLift, (s as Sprite & { hillLift?: number }).hillLift ?? 0)
            left = Math.min(left, s.x)
            top = Math.min(top, s.y)
            right = Math.max(right, s.x + s.width)
            bottom = Math.max(bottom, s.y + s.height)
            treeZ = Math.max(treeZ, s.zIndex)
            alpha = Math.max(alpha, s.alpha)
        }
        const side = felling.side < 0 ? -1 : 1
        const felled = felling.felled
        // The cut art describes the whole composite; a set that is not that size is not that art.
        const fits = felled && Math.abs(felled.art.width - (right - left)) <= 1 && Math.abs(felled.art.height - (bottom - top)) <= 1

        const crown = new Container()
        crown.zIndex = treeZ + 0.1
        let art: TreeArt
        let hingeArtX: number
        let hingeArtY: number
        let drop: number
        let stump: Sprite | undefined
        let shadow: Sprite | undefined
        let piece: Sprite | undefined
        if (fits) {
            art = felled.art
            hingeArtX = felled.hingeX
            hingeArtY = felled.cutY
            drop = Math.max(0, art.footY - felled.cutY)
            piece = new Sprite(felled.crown)
            piece.position.set(-hingeArtX, -hingeArtY)
            crown.addChild(piece)
            stump = new Sprite(felled.stump)
            stump.position.set(left, top)
            stump.zIndex = treeZ
            if (felled.shadow) {
                shadow = new Sprite(felled.shadow)
                shadow.position.set(left, top)
                shadow.zIndex = treeZ - 0.1
            }
        } else {
            art = fallbackTreeArt(right - left, bottom - top)
            const halfTrunk = (art.trunkRight - art.trunkLeft + 1) / 2
            hingeArtX = (art.trunkLeft + art.trunkRight + 1) / 2 + side * halfTrunk
            hingeArtY = art.footY
            drop = 0
            for (const s of set) {
                const copy = new Sprite(s.texture)
                copy.position.set(s.x - left - hingeArtX, s.y - top - hingeArtY)
                copy.scale.copyFrom(s.scale)
                crown.addChild(copy)
            }
        }

        const fall: Fall = {
            key: felling.key,
            side,
            crown,
            piece,
            breakup: fits ? felled.breakup : undefined,
            stump,
            shadow,
            shadowAlpha: alpha,
            art,
            left,
            top,
            hillLift,
            hingeArtX,
            hingeArtY,
            hingeX: left + hingeArtX,
            hingeY: top + hingeArtY,
            drop,
            // TreeHit's lean is the tangent of the angle; only a lean the way it is going counts.
            angle: Math.max(0.02, Math.min(0.08, Math.atan(side * (felling.lean || 0)))),
            velocity: this.tuning.startSpeed,
            snapped: false,
            impacts: 0,
            hitMs: 0,
            landedMs: 0,
            fadeStartMs: 0,
            stumpFadeMs: 0,
            // The occluder fade may have had it see-through; it comes back solid as it goes.
            alphaFrom: alpha || 1,
            bornMs: nowMs,
            zFloor: treeZ + 0.5,
            owed: 0,
        }
        if (shadow) this.surface.addChild(shadow)
        if (stump) this.surface.addChild(stump)
        this.surface.addChild(crown)
        this.falls.set(fall.key, fall)
        this.draw(fall, nowMs)
        return true
    }

    /** Advance every fall. Called once per drawn frame. */
    update = (nowMs: number = performance.now()) => {
        if (!this.tuning.enabled) { this.clear(); return }
        if (this.lastMs === undefined) {
            this.lastMs = nowMs
            if (!this.falls.size) return
        }
        const dt = Math.min(Math.max(0, (nowMs - this.lastMs) / 1000), MAX_FRAME_SECONDS)
        this.lastMs = nowMs
        this.falls.forEach(fall => {
            // A floor change can destroy the surface, and the stump and shadow on it with it.
            if (fall.stump?.destroyed) fall.stump = undefined
            if (fall.shadow?.destroyed) fall.shadow = undefined
            if (!fall.landedMs && !fall.crown.destroyed) this.topple(fall, dt, nowMs)
            if (fall.hitMs) this.clearAway(fall, dt, nowMs)
            if (fall.crown.destroyed && !fall.stump) this.falls.delete(fall.key)
            else this.draw(fall, nowMs)
        })
    }

    /** Take every falling tree away at once (a new room). */
    clear = () => {
        Array.from(this.falls.keys()).forEach(this.remove)
        this.lastMs = undefined
    }

    // ---------------------------------------------------------------------------------------------
    // Going over
    // ---------------------------------------------------------------------------------------------

    private topple = (fall: Fall, dt: number, nowMs: number) => {
        const before = fall.angle
        for (let left = dt; left > 1e-6; left -= STEP) {
            const h = Math.min(STEP, left)
            fall.velocity += this.tuning.gravity * Math.sin(fall.angle) * h
            fall.angle += fall.velocity * h
            if (fall.angle >= QUARTER && fall.velocity > 0) {
                fall.angle = QUARTER
                if (fall.velocity < SETTLE_SPEED || fall.impacts >= 2) {
                    fall.velocity = 0
                    fall.landedMs = nowMs
                    break
                }
                this.crash(fall, fall.velocity, nowMs)
                fall.velocity = -fall.velocity * this.tuning.bounce
            }
        }
        if (!fall.snapped && fall.angle >= SNAP_ANGLE) {
            fall.snapped = true
            this.snap(fall, nowMs)
        }
        // Leaves stream off the canopy in proportion to how far it moved this frame.
        fall.owed += this.tuning.trail * Math.abs(fall.angle - before)
        while (fall.owed >= 1) {
            fall.owed -= 1
            this.shedLeaf(fall, nowMs)
        }
    }

    // Timed from the first crash, not from coming to rest: the break-up may begin while it still bounces.
    private clearAway = (fall: Fall, dt: number, nowMs: number) => {
        const lying = nowMs - fall.hitMs
        if (!fall.crown.destroyed) {
            if (lying >= this.tuning.restMs) {
                const before = fall.fadeStartMs ? (nowMs - fall.fadeStartMs - dt * 1000) / this.tuning.fadeMs : 0
                if (!fall.fadeStartMs) {
                    fall.fadeStartMs = nowMs
                    // Art that cannot break up fades instead, letting a few leaves go as it does.
                    if (!fall.breakup) for (let i = 0; i < Math.round(this.tuning.leaves * 0.35); i++) this.shedLeaf(fall, nowMs, true)
                }
                const t = (nowMs - fall.fadeStartMs) / this.tuning.fadeMs
                const piece = fall.piece
                const breakup = fall.breakup
                if (piece && breakup) {
                    const stage = Math.floor(t * (breakup.stages + 1))
                    if (stage >= 1 && stage <= breakup.stages) piece.texture = breakup.stage(stage - 1)
                    // Leaves go up off the crown where it is coming apart.
                    this.shedBreakup(fall, Math.max(0, before), Math.min(1, t), nowMs)
                }
                if (t >= 1) {
                    fall.crown.destroy({ children: true })
                    fall.shadow?.destroy()
                    fall.shadow = undefined
                }
            }
        }
        if (fall.stump && lying >= this.tuning.stumpMs) {
            if (!fall.stumpFadeMs) fall.stumpFadeMs = nowMs
            if (nowMs - fall.stumpFadeMs >= STUMP_FADE_MS) {
                fall.stump.destroy()
                fall.stump = undefined
            }
        }
    }

    // Where the crown is this frame: the hinge, carried down to the ground as the butt comes off the
    // stump near the end, and the turn.
    private pose = (fall: Fall) => {
        const off = smoothstep(0.6, 1, fall.angle / QUARTER)
        return {
            x: fall.hingeX - fall.side * 2 * off,
            y: fall.hingeY + fall.drop * off,
            rotation: fall.side * fall.angle,
        }
    }

    // A point of the art, where the falling crown has it in the world now.
    private place = (fall: Fall, artX: number, artY: number) => {
        const pose = this.pose(fall)
        const lx = artX - fall.hingeArtX
        const ly = artY - fall.hingeArtY
        const cos = Math.cos(pose.rotation)
        const sin = Math.sin(pose.rotation)
        return { x: pose.x + lx * cos - ly * sin, y: pose.y + lx * sin + ly * cos, lx, ly }
    }

    private draw = (fall: Fall, nowMs: number) => {
        const crown = fall.crown
        if (!crown.destroyed) {
            const pose = this.pose(fall)
            crown.position.set(pose.x, pose.y)
            crown.rotation = pose.rotation
            const solid = Math.min(1, (nowMs - fall.bornMs) / 250)
            let alpha = fall.alphaFrom + (1 - fall.alphaFrom) * solid
            if (fall.fadeStartMs && !fall.breakup) alpha *= 1 - smoothstep(0, 1, (nowMs - fall.fadeStartMs) / this.tuning.fadeMs)
            crown.alpha = alpha
        }
        if (fall.shadow) fall.shadow.alpha = fall.shadowAlpha * Math.max(0, Math.cos(fall.angle)) ** 2
        if (fall.stump && fall.stumpFadeMs) {
            const t = Math.min(1, (nowMs - fall.stumpFadeMs) / STUMP_FADE_MS)
            fall.stump.alpha = 1 - t * t
        }
    }

    // ---------------------------------------------------------------------------------------------
    // What comes off it
    // ---------------------------------------------------------------------------------------------

    private ground = (fall: Fall) => fall.top + fall.art.footY

    private bark = (fall: Fall) => fall.art.barkColors.length ? fall.art.barkColors : FRESH_WOOD

    // The canopy's greens, less its darkest: that one is the outline and the deep shade, and a leaf in
    // the air painted with it reads as a black speck.
    private leafColors = (fall: Fall) => {
        const colors = fall.art.canopyColors
        return colors.length > 2 ? colors.slice(1) : colors.length ? colors : [0x548c3a]
    }

    // How far the lying crown reaches across the ground toward the camera from the line it lies on: the
    // canopy on the far side of the hinge, turned down.
    private nearReach = (fall: Fall) => fall.side > 0 ? fall.art.width - fall.hingeArtX : fall.hingeArtX

    // The hinge breaks: splinters out of the cut, both ways, and a breath of sawdust.
    private snap = (fall: Fall, nowMs: number) => {
        const ground = this.ground(fall)
        const height = Math.max(4, fall.drop)
        const trunkX = fall.left + (fall.art.trunkLeft + fall.art.trunkRight + 1) / 2
        for (let i = 0; i < 7; i++) {
            const way = i < 5 ? -fall.side : fall.side
            if (!this.debris.chip({
                x: trunkX + between(-2, 2),
                groundY: ground + between(0, 3),
                z: height,
                vx: way * between(25, 80),
                vy: between(-8, 24),
                vz: between(50, 120),
                zFloor: fall.zFloor,
            }, Math.random() < 0.7 ? pick(FRESH_WOOD) : pick(this.bark(fall)), nowMs)) break
        }
        treeFallFeedback.creaked({ x: trunkX, y: ground + fall.hillLift })
    }

    private shedLeaf = (fall: Fall, nowMs: number, lying = false) => {
        const points = fall.art.canopyPoints
        if (points.length < 2) return
        const p = 2 * Math.floor(Math.random() * (points.length / 2))
        const at = this.place(fall, points[p], points[p + 1])
        const ground = this.ground(fall)
        // The canopy's own motion, which flings what it lets go of.
        const spin = fall.side * fall.velocity
        const vx = -(at.lx * Math.sin(fall.side * fall.angle) + at.ly * Math.cos(fall.side * fall.angle)) * spin
        const vy = (at.lx * Math.cos(fall.side * fall.angle) - at.ly * Math.sin(fall.side * fall.angle)) * spin
        const colors = this.leafColors(fall)
        // Lying down, the canopy IS on the ground: a leaf lets go from where it lies.
        const groundY = lying ? at.y + between(0, 4) : ground + between(-2, 12)
        this.debris.leaf({
            x: at.x,
            groundY,
            z: lying ? between(2, 8) : Math.max(4, groundY - at.y),
            vx: 0.45 * vx + between(-8, 8),
            vy: between(-4, 8),
            vz: lying ? between(20, 50) : Math.max(-40, Math.min(40, -0.45 * vy)),
            zFloor: fall.zFloor,
        }, pick(colors), nowMs)
    }

    // Leaves lifting off the crown where its pixels went between break-up times `from` and `to`.
    private shedBreakup = (fall: Fall, from: number, to: number, nowMs: number) => {
        const points = fall.art.canopyPoints
        if (to <= from || points.length < 2) return
        const reach = crownReach(fall.art.width, fall.hingeArtX, fall.hingeArtY)
        const colors = this.leafColors(fall)
        // A fixed share of the canopy's sample points is tried each frame; those that went in the window shed.
        for (let tries = 0; tries < 14; tries++) {
            const p = 2 * Math.floor(Math.random() * (points.length / 2))
            const order = crownBreakupOrder(points[p], points[p + 1], fall.hingeArtX, fall.hingeArtY, reach)
            if (order < from || order >= to || Math.random() > this.tuning.breakupLeaves) continue
            const at = this.place(fall, points[p], points[p + 1])
            this.debris.leaf({
                x: at.x,
                groundY: at.y + between(0, 4),
                z: between(1, 6),
                vx: between(-14, 14),
                vy: between(-6, 10),
                vz: between(25, 60),
                zFloor: fall.zFloor,
            }, pick(colors), nowMs)
        }
    }

    // The crash. The first is the big one; the bounce comes down again with a fraction of it.
    private crash = (fall: Fall, speed: number, nowMs: number) => {
        fall.impacts++
        const first = fall.impacts === 1
        if (first) fall.hitMs = nowMs
        const force = Math.min(1.4, speed / 5) * (first ? 1 : 0.3)
        const ground = this.ground(fall)
        const art = fall.art
        // Along the fallen tree, butt to tip: the trunk, then the canopy.
        const butt = fall.hingeX - fall.side * 2
        const length = fall.hingeArtY
        const trunkLength = Math.max(0, fall.hingeArtY - art.canopyBottomY)
        const along = (d: number) => butt + fall.side * d
        // Dust rolls out from under it where it can be seen: along the near edge of the fallen crown, and
        // off the end of it.
        const near = this.nearReach(fall)
        const dust = Math.round(this.tuning.dust * force)
        for (let i = 0; i < dust; i++) {
            const d = length * ((i + Math.random()) / Math.max(1, dust))
            const inCanopy = d > trunkLength
            const tip = i === dust - 1
            this.debris.puff({
                x: along(tip ? length + 4 : d) + between(-3, 3),
                groundY: ground + (inCanopy ? near * between(0.6, 0.95) : between(1, 4)),
                z: between(0, 2),
                // Pushed out from under it: toward the camera, and off the end.
                vx: fall.side * (tip ? between(20, 40) : between(-8, 16)),
                vy: between(10, 30),
                vz: between(6, 16),
                zFloor: fall.zFloor,
            }, Math.random() < 0.75 ? DUST : DIRT, nowMs, {
                scale: between(0.45, 0.7) * (first ? 1 : 0.6),
                alpha: between(0.3, 0.45),
                lifeMs: between(700, 1100),
                growth: between(1.9, 2.5),
            })
        }
        if (!first) return

        const points = art.canopyPoints
        const colors = this.leafColors(fall)
        const middle = along(length / 2)
        const leaves = Math.round(this.tuning.leaves * force)
        for (let i = 0; i < leaves && points.length >= 2; i++) {
            const p = 2 * Math.floor(Math.random() * (points.length / 2))
            const at = this.place(fall, points[p], points[p + 1])
            this.debris.leaf({
                x: at.x,
                groundY: at.y + between(0, 6),
                z: between(2, 10),
                vx: Math.sign(at.x - middle) * between(10, 45) + between(-10, 10),
                vy: between(-15, 20),
                vz: between(45, 115) * force,
                zFloor: fall.zFloor,
            }, pick(colors), nowMs)
        }
        // Twigs and bark knocked off along the trunk and out of the crown.
        for (let i = 0; i < 6; i++) {
            const d = length * Math.random()
            if (!this.debris.chip({
                x: along(d),
                groundY: ground + between(-4, 6),
                z: between(2, 6),
                vx: between(-40, 40),
                vy: between(-15, 25),
                vz: between(60, 130) * force,
                zFloor: fall.zFloor,
            }, pick(this.bark(fall)), nowMs, { w: between(2.5, 4.5), h: between(1, 1.5) })) break
        }
        treeFallFeedback.landed({ x: middle, y: ground + fall.hillLift }, Math.min(1.5, (art.height / 128) * force))
    }

    private remove = (key: string) => {
        const fall = this.falls.get(key)
        if (!fall) return
        if (!fall.crown.destroyed) fall.crown.destroy({ children: true })
        if (fall.stump && !fall.stump.destroyed) fall.stump.destroy()
        if (fall.shadow && !fall.shadow.destroyed) fall.shadow.destroy()
        this.falls.delete(key)
    }
}
